<script lang="ts">
export const elAccordionColors = ['primary', 'secondary'] as const;
export type ElAccordionColor = (typeof elAccordionColors)[number];
</script>

<script lang="ts" setup>
import { ref, computed, useSlots } from 'vue';
import ElIcon from '@/ElIcon.vue';

const slots = useSlots();

const props = withDefaults(
  defineProps<{
    label?: string;
    open?: boolean;
    color?: ElAccordionColor;
  }>(),
  {
    label: undefined,
    color: 'primary',
  },
);

const emit = defineEmits<{
  (event: 'update:open', open?: boolean): void;
}>();

const openLocal = ref<boolean>(false);

const openProxy = computed<boolean>({
  set(open: boolean) {
    emit('update:open', open);
    openLocal.value = open;
  },
  get(): boolean {
    return props.open || openLocal.value;
  },
});
</script>

<template>
  <div class="relative rounded-md bg-white overflow-hidden">
    <button class="w-full h-10" @click.prevent="openProxy = !openProxy">
      <div
        class="h-full w-full flex justify-between items-center rounded-md cursor-pointer px-2 select-none active:bg-neutral-pressed active:text-neutral-darker hover:bg-neutral-hover"
        :class="[
          color === 'primary' && !openProxy && 'bg-neutral-surface text-neutral-darker',
          color === 'primary' && openProxy && 'bg-neutral-active text-primary-active',
          color === 'secondary' && !openProxy && 'bg-neutral-surface text-neutral-darker',
          color === 'secondary' && openProxy && 'bg-neutral-active text-secondary-active',
        ]"
      >
        <span v-if="slots.header" class="text-sm font-medium"><slot name="header" /></span>
        <span v-else class="text-ellipsis whitespace-nowrap overflow-hidden text-sm font-medium">{{ label }}</span>

        <ElIcon solid :name="openProxy ? 'ChevronUpIcon' : 'ChevronDownIcon'" class="w-4 h-4" />
      </div>
    </button>

    <div class="overflow-hidden w-full">
      <Transition
        enter-active-class="ease-out"
        enter-from-class="-translate-y-full"
        enter-to-class="translate-y-0"
        leave-active-class="ease-in"
        leave-from-class="translate-y-0"
        leave-to-class="-translate-y-full"
        :duration="100"
      >
        <div v-show="openProxy" class="transform duration-100 w-full flex flex-col gap-2 p-2 bg-white">
          <slot />
        </div>
      </Transition>
    </div>
  </div>
</template>
